import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Motorista, Veiculo } from '../../types';
import { veiculosService } from '../../services/veiculos';
import { motoristasService } from '../../services/motoristas';
import { useConfirm } from '../../components/ConfirmDialog';
import { mensagemDoErro } from '../../utils/erros';
import { parseApiDate, formatLiters, formatConsumption } from '../../utils/formatters';

const VeiculoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('geral');
  const [motoristaEscolhido, setMotoristaEscolhido] = useState('');

  const { data: veiculoData, isLoading, error } = useQuery({
    queryKey: ['veiculo', id],
    queryFn: () => veiculosService.getVeiculo(Number(id)),
  });

  // Sem atribuicao o motorista nao ve o veiculo no portal e nao consegue lancar
  // nada: a lista existia so para consulta, e nao havia tela nenhuma para criar
  // o vinculo.
  const { data: motoristasData } = useQuery({
    queryKey: ['motoristas', 'para-atribuir'],
    queryFn: () => motoristasService.getMotoristas({ status: 'ativo', per_page: 500 }),
    enabled: activeTab === 'motoristas',
  });

  const atualizarVeiculo = () => {
    queryClient.invalidateQueries({ queryKey: ['veiculo', id] });
    queryClient.invalidateQueries({ queryKey: ['veiculos'] });
  };

  const atribuir = useMutation({
    mutationFn: (motoristaId: number) => veiculosService.atribuirMotorista(Number(id), motoristaId),
    onSuccess: () => {
      toast.success('Motorista atribuído ao veículo.');
      setMotoristaEscolhido('');
      atualizarVeiculo();
    },
    onError: (erro) => toast.error(mensagemDoErro(erro, 'Não foi possível atribuir o motorista.')),
  });

  const remover = useMutation({
    mutationFn: (motoristaId: number) => veiculosService.removerMotorista(Number(id), motoristaId),
    onSuccess: () => {
      toast.success('Motorista removido do veículo.');
      atualizarVeiculo();
    },
    onError: (erro) => toast.error(mensagemDoErro(erro, 'Não foi possível remover o motorista.')),
  });

  // Tratar erros
  useEffect(() => {
    if (error) {
      toast.error('Erro ao carregar dados do veículo');
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !veiculoData?.data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Erro ao carregar dados do veículo</p>
        <button
          onClick={() => navigate('/veiculos')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Voltar para Lista
        </button>
      </div>
    );
  }

  const veiculo: Veiculo = veiculoData.data;
  const estatisticas = veiculoData.estatisticas;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo': return 'bg-green-100 text-green-800';
      case 'inativo': return 'bg-gray-100 text-gray-800';
      case 'manutencao': return 'bg-yellow-100 text-yellow-800';
      case 'vendido': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoriaColor = (categoria: string) => {
    switch (categoria) {
      case 'passeio': return 'bg-blue-100 text-blue-800';
      case 'utilitario': return 'bg-purple-100 text-purple-800';
      case 'comercial': return 'bg-indigo-100 text-indigo-800';
      case 'motocicleta': return 'bg-pink-100 text-pink-800';
      case 'caminhao': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (value: number | string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(value));
  };

  const formatDate = (date: string) => {
    const d = parseApiDate(date);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  const checkDocumentStatus = (date: string | null) => {
    if (!date) return { status: 'sem_data', color: 'bg-gray-500' };
    
    const today = new Date();
    const dueDate = new Date(date);
    const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
    
    if (daysUntilDue < 0) return { status: 'vencido', color: 'bg-red-500' };
    if (daysUntilDue <= 30) return { status: 'proximo_vencimento', color: 'bg-yellow-500' };
    return { status: 'em_dia', color: 'bg-green-500' };
  };

  const tabs = [
    { id: 'geral', name: 'Geral', icon: '🚗' },
    { id: 'motoristas', name: 'Motoristas', icon: '👤' },
    { id: 'abastecimentos', name: 'Abastecimentos', icon: '⛽' },
    { id: 'manutencoes', name: 'Manutenções', icon: '🔧' },
    { id: 'despesas', name: 'Despesas', icon: '💰' },
    { id: 'viagens', name: 'Viagens', icon: '🛣️' },
    { id: 'documentos', name: 'Documentos', icon: '📄' },
  ];

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {veiculo.marca} {veiculo.modelo}
            </h1>
            <p className="text-gray-600 mt-2">
              Placa: {veiculo.placa} • {veiculo.ano_fabricacao}/{veiculo.ano_modelo}
            </p>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/veiculos/${id}/editar`}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Editar
            </Link>
            <button
              onClick={() => navigate('/veiculos')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Voltar
            </button>
          </div>
        </div>
      </div>

      {/* Status e Categoria */}
      <div className="mb-6 flex space-x-4">
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(veiculo.status)}`}>
          {veiculo.status}
        </span>
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getCategoriaColor(veiculo.categoria)}`}>
          {veiculo.categoria}
        </span>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border">
        {activeTab === 'geral' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Informações Gerais</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Informações Básicas */}
              <div>
                <h3 className="text-lg font-medium mb-3">Informações Básicas</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Placa</dt>
                    <dd className="text-sm text-gray-900">{veiculo.placa}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Marca/Modelo</dt>
                    <dd className="text-sm text-gray-900">{veiculo.marca} {veiculo.modelo}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Ano</dt>
                    <dd className="text-sm text-gray-900">{veiculo.ano_fabricacao}/{veiculo.ano_modelo}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Cor</dt>
                    <dd className="text-sm text-gray-900">{veiculo.cor}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Combustível</dt>
                    <dd className="text-sm text-gray-900">{veiculo.combustivel}</dd>
                  </div>
                </dl>
              </div>

              {/* Informações Técnicas */}
              <div>
                <h3 className="text-lg font-medium mb-3">Informações Técnicas</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Chassi</dt>
                    <dd className="text-sm text-gray-900 font-mono">{veiculo.chassi}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Renavam</dt>
                    <dd className="text-sm text-gray-900 font-mono">{veiculo.renavam}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Quilometragem</dt>
                    <dd className="text-sm text-gray-900">{Number(veiculo.quilometragem).toLocaleString('pt-BR')} km</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Capacidade do Tanque</dt>
                    <dd className="text-sm text-gray-900">{formatLiters(veiculo.capacidade_tanque)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Consumo Médio</dt>
                    <dd className="text-sm text-gray-900">
                      {veiculo.consumo_medio ? formatConsumption(veiculo.consumo_medio) : 'Não informado'}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Informações Financeiras */}
              <div>
                <h3 className="text-lg font-medium mb-3">Informações Financeiras</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Valor de Compra</dt>
                    <dd className="text-sm text-gray-900">
                      {veiculo.valor_compra ? formatCurrency(veiculo.valor_compra) : 'Não informado'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Valor FIPE</dt>
                    <dd className="text-sm text-gray-900">
                      {veiculo.valor_fipe ? formatCurrency(veiculo.valor_fipe) : 'Não informado'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Data de Compra</dt>
                    <dd className="text-sm text-gray-900">
                      {veiculo.data_compra ? formatDate(veiculo.data_compra) : 'Não informado'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Estatísticas */}
            {estatisticas && (
              <div className="mt-8">
                <h3 className="text-lg font-medium mb-4">Estatísticas</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{estatisticas.total_abastecimentos}</div>
                    <div className="text-sm text-blue-600">Abastecimentos</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{estatisticas.total_manutencoes}</div>
                    <div className="text-sm text-green-600">Manutenções</div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">{estatisticas.total_despesas}</div>
                    <div className="text-sm text-yellow-600">Despesas</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{estatisticas.total_viagens}</div>
                    <div className="text-sm text-purple-600">Viagens</div>
                  </div>
                </div>
              </div>
            )}

            {/* Observações */}
            {veiculo.observacoes && (
              <div className="mt-8">
                <h3 className="text-lg font-medium mb-3">Observações</h3>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                  {veiculo.observacoes}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'motoristas' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-1">Motoristas atribuídos</h2>
            <p className="text-sm text-gray-600 mb-4">
              O motorista só enxerga no portal — e só consegue lançar abastecimento e despesa — os veículos
              atribuídos a ele aqui.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <select
                value={motoristaEscolhido}
                onChange={(e) => setMotoristaEscolhido(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione um motorista...</option>
                {(motoristasData?.data ?? [])
                  .filter((m: Motorista) => !(veiculo.motoristas ?? []).some((atual: any) => atual.id === m.id))
                  .map((m: Motorista) => (
                    <option key={m.id} value={m.id}>
                      {m.nome} — CNH {m.categoria_cnh}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                disabled={!motoristaEscolhido || atribuir.isPending}
                onClick={() => atribuir.mutate(Number(motoristaEscolhido))}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {atribuir.isPending ? 'Atribuindo...' : 'Atribuir ao veículo'}
              </button>
            </div>

            {veiculo.motoristas && veiculo.motoristas.length > 0 ? (
              <div className="space-y-4">
                {veiculo.motoristas.map((motorista: any) => (
                  <div key={motorista.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <h3 className="font-medium">{motorista.nome}</h3>
                        <p className="text-sm text-gray-600">CNH: {motorista.cnh}</p>
                        <p className="text-sm text-gray-600">Categoria: {motorista.categoria_cnh}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          motorista.status === 'ativo' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {motorista.status}
                        </span>
                        <button
                          type="button"
                          disabled={remover.isPending}
                          onClick={async () => {
                            const confirmado = await confirm({
                              title: 'Remover do veículo',
                              message: `${motorista.nome} deixa de ver este veículo no portal e não poderá mais lançar abastecimento nem despesa para ele. Os lançamentos já feitos continuam no histórico.`,
                              confirmLabel: 'Remover',
                              variant: 'danger',
                            });
                            if (confirmado) remover.mutate(motorista.id);
                          }}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Nenhum motorista atribuído a este veículo — nenhum deles consegue lançar nada para ele no portal.
              </p>
            )}
          </div>
        )}

        {activeTab === 'abastecimentos' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Histórico de Abastecimentos</h2>
            {veiculo.abastecimentos && veiculo.abastecimentos.length > 0 ? (
              <div className="space-y-4">
                {veiculo.abastecimentos.map((abastecimento: any) => (
                  <div key={abastecimento.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium">{abastecimento.posto}</h3>
                        <p className="text-sm text-gray-600">
                          {formatDate(abastecimento.data_abastecimento)} • {formatLiters(abastecimento.litros)} • {formatCurrency(abastecimento.valor_total)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Quilometragem: {Number(abastecimento.quilometragem).toLocaleString('pt-BR')} km
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        abastecimento.tanque_cheio ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {abastecimento.tanque_cheio ? 'Tanque Cheio' : 'Abastecimento Parcial'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nenhum abastecimento registrado.</p>
            )}
          </div>
        )}

        {activeTab === 'manutencoes' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Histórico de Manutenções</h2>
            {veiculo.manutencoes && veiculo.manutencoes.length > 0 ? (
              <div className="space-y-4">
                {veiculo.manutencoes.map((manutencao: any) => (
                  <div key={manutencao.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium">{manutencao.descricao}</h3>
                        <p className="text-sm text-gray-600">
                          {formatDate(manutencao.data_manutencao)} • {manutencao.oficina_nome} • {formatCurrency(manutencao.valor_total)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Tipo: {manutencao.tipo} • Quilometragem: {Number(manutencao.quilometragem).toLocaleString('pt-BR')} km
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        manutencao.status === 'concluida' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {manutencao.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nenhuma manutenção registrada.</p>
            )}
          </div>
        )}

        {activeTab === 'despesas' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Histórico de Despesas</h2>
            {veiculo.despesas && veiculo.despesas.length > 0 ? (
              <div className="space-y-4">
                {veiculo.despesas.map((despesa: any) => (
                  <div key={despesa.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium">{despesa.descricao}</h3>
                        <p className="text-sm text-gray-600">
                          Data: {formatDate(despesa.data_despesa)} • {formatCurrency(despesa.valor)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Categoria: {despesa.categoria}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        despesa.status === 'paga' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {despesa.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nenhuma despesa registrada.</p>
            )}
          </div>
        )}

        {activeTab === 'viagens' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Histórico de Viagens</h2>
            {veiculo.viagens && veiculo.viagens.length > 0 ? (
              <div className="space-y-4">
                {veiculo.viagens.map((viagem: any) => (
                  <div key={viagem.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium">{viagem.origem} → {viagem.destino}</h3>
                        <p className="text-sm text-gray-600">
                          {formatDate(viagem.data_saida)} • {viagem.km_percorridos || 0} km • {formatCurrency(viagem.valor_total || 0)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Motorista: {viagem.motorista?.nome || 'Não informado'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        viagem.status === 'concluida' ? 'bg-green-100 text-green-800' : 
                        viagem.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {viagem.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nenhuma viagem registrada.</p>
            )}
          </div>
        )}

        {activeTab === 'documentos' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Status dos documentos</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Licenciamento */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-3">Licenciamento</h3>
                {veiculo.vencimento_licenciamento ? (
                  <div>
                    <div className="flex items-center mb-2">
                      <span className={`w-3 h-3 rounded-full mr-2 ${checkDocumentStatus(veiculo.vencimento_licenciamento).color}`}></span>
                      <span className="text-sm font-medium">
                        {checkDocumentStatus(veiculo.vencimento_licenciamento).status === 'vencido' ? 'Vencido' :
                         checkDocumentStatus(veiculo.vencimento_licenciamento).status === 'proximo_vencimento' ? 'Próximo do Vencimento' : 'Em Dia'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Vencimento: {formatDate(veiculo.vencimento_licenciamento)}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Data de vencimento não informada</p>
                )}
              </div>

              {/* Seguro */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-3">Seguro</h3>
                {veiculo.vencimento_seguro ? (
                  <div>
                    <div className="flex items-center mb-2">
                      <span className={`w-3 h-3 rounded-full mr-2 ${checkDocumentStatus(veiculo.vencimento_seguro).color}`}></span>
                      <span className="text-sm font-medium">
                        {checkDocumentStatus(veiculo.vencimento_seguro).status === 'vencido' ? 'Vencido' :
                         checkDocumentStatus(veiculo.vencimento_seguro).status === 'proximo_vencimento' ? 'Próximo do Vencimento' : 'Em Dia'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Vencimento: {formatDate(veiculo.vencimento_seguro)}
                    </p>
                    {veiculo.seguradora && (
                      <p className="text-sm text-gray-600">
                        Seguradora: {veiculo.seguradora}
                      </p>
                    )}
                    {veiculo.apolice_seguro && (
                      <p className="text-sm text-gray-600">
                        Apólice: {veiculo.apolice_seguro}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Data de vencimento não informada</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VeiculoDetail;
